import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Coins, Plus, ArrowUp, ArrowDown, RotateCcw, Sparkles, Zap, AlertTriangle, ShieldCheck } from "lucide-react";

export default function CreditsPage() {
  const { user, loading } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [busyPack, setBusyPack] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/credits/me").then(({ data }) => setWallet(data));
    api.get("/credits/ledger?limit=30").then(({ data }) => setLedger(data.items || []));
    api.get("/credits/pricing").then(({ data }) => setPricing(data));
    // Refresh on returning from Stripe checkout success
    const params = new URLSearchParams(window.location.search);
    if (params.get("purchase") === "success") {
      setTimeout(() => {
        api.get("/credits/me").then(({ data }) => setWallet(data));
        api.get("/credits/ledger?limit=30").then(({ data }) => setLedger(data.items || []));
      }, 1500);
    }
  }, [user]);

  const onBuyPack = async (sku) => {
    setError(""); setBusyPack(sku);
    try {
      const { data } = await api.post("/credits/checkout", { sku });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusyPack(null);
    }
  };

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/credits" replace />;

  const balance = wallet?.balance ?? 0;
  const dailyRemaining = wallet?.daily?.remaining_today ?? 0;
  const dailyCap = wallet?.daily?.daily_cap ?? 0;
  const lowBalance = balance < 50;

  return (
    <DashboardShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Wallet</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Credits & usage</h1>
        <p className="text-[#A0A0A0] mt-4 max-w-2xl">Credits power every render. Top up anytime — packs start at $5. Failed generations refund automatically.</p>
      </div>

      {error && (
        <div className="mb-5 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="credits-error">{error}</div>
      )}

      {lowBalance && (
        <div data-testid="low-balance-banner"
             className="mb-6 px-4 py-3 border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <b>Running low — {balance} credits left.</b> Grab a $5 starter pack below to keep generating.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] p-8" data-testid="wallet-card">
          <div className="flex items-center gap-3 mb-4">
            <Coins className="w-5 h-5 text-[#D4AF37]" />
            <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Available</p>
          </div>
          <p className="font-display text-7xl md:text-8xl font-black text-[#F4F4F0] mb-3" data-testid="wallet-balance">{balance.toLocaleString()}</p>
          <p className="text-[#A0A0A0] text-sm">credits</p>
          <div className="mt-6 pt-6 border-t border-[#2A2A2A] grid grid-cols-3 gap-4 text-xs">
            <Stat label="Today used" value={(wallet?.daily?.spent_today ?? 0).toLocaleString()} />
            <Stat label="Daily cap" value={dailyCap.toLocaleString()} />
            <Stat label="Remaining today" value={dailyRemaining.toLocaleString()} highlight />
          </div>
        </div>

        <div className="border border-[#2A2A2A] bg-[#121212] p-6 flex flex-col" data-testid="lifetime-card">
          <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-5">Lifetime</p>
          <div className="space-y-4 flex-1">
            <RowStat label="Total added" value={(wallet?.lifetime_added ?? 0).toLocaleString()} icon={ArrowUp} color="text-emerald-300" />
            <RowStat label="Total spent" value={(wallet?.lifetime_spent ?? 0).toLocaleString()} icon={ArrowDown} color="text-[#D4AF37]" />
            <RowStat label="Refunded" value={(wallet?.lifetime_refunded ?? 0).toLocaleString()} icon={RotateCcw} color="text-blue-300" />
          </div>
        </div>
      </div>

      {pricing?.packs?.length > 0 && (
        <div className="mb-10">
          <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-1">Top up — Pay-As-You-Go</p>
              <p className="text-[11px] text-[#5A5A5A]">100 credits = $5 · Bigger packs include <span className="text-[#D4AF37]">bonus credits</span></p>
            </div>
            <Link to="/pricing" className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:underline">Compare with membership →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="credit-packs">
            {pricing.packs.filter((p) => !p.admin_only).map((p) => {
              const base  = Number(p.base_credits  ?? p.credits);
              const bonus = Number(p.bonus_credits ?? 0);
              const total = Number(p.credits);
              const badge = p.badge;
              const popular  = badge === "Most Popular";
              const best     = badge === "Best Value";
              return (
                <button
                  key={p.sku}
                  onClick={() => onBuyPack(p.sku)}
                  disabled={busyPack !== null}
                  data-testid={`pack-${p.sku}`}
                  className={`relative text-left border bg-[#121212] p-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group ${
                    popular ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/40"
                    : best  ? "border-emerald-500/60 ring-1 ring-emerald-500/30"
                    : "border-[#2A2A2A] hover:border-[#D4AF37]"
                  }`}
                >
                  {badge && (
                    <span className={`absolute -top-2 left-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 ${
                      popular ? "bg-[#D4AF37] text-[#080808]" : "bg-emerald-500 text-[#080808]"
                    }`} data-testid={`pack-badge-${p.sku}`}>{badge}</span>
                  )}
                  <p className="font-display text-3xl font-bold text-[#F4F4F0]">{total.toLocaleString()}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mt-1">credits total</p>
                  {bonus > 0 ? (
                    <p className="text-[10px] text-[#D4AF37] mt-2" data-testid={`pack-bonus-${p.sku}`}>
                      {base.toLocaleString()} base <span className="text-[#5A5A5A]">+</span> <b>+{bonus.toLocaleString()} bonus</b>
                    </p>
                  ) : (
                    <p className="text-[10px] text-[#5A5A5A] mt-2">{base.toLocaleString()} base · no bonus</p>
                  )}
                  <div className="mt-3 pt-3 border-t border-[#1A1A1A] flex items-end justify-between">
                    <span className="font-display text-lg font-bold text-[#F4F4F0]">${p.price_usd}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] group-hover:underline">
                      {busyPack === p.sku ? "Opening…" : "Buy →"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-3">Credits never expire · Failed renders auto-refund · Bonus credits included on packs above $10</p>

          {/* Admin-only test pack — only renders when the backend returned an admin_only entry
              (i.e. the viewer is owner). Public users never see this row. */}
          {pricing.packs.some((p) => p.admin_only) && (
            <div className="mt-6 border border-purple-500/40 bg-purple-500/5 p-4" data-testid="admin-test-pack">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-3.5 h-3.5 text-purple-300" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-purple-300 font-bold">Admin · Internal Only</p>
              </div>
              <p className="text-xs text-[#A0A0A0] mb-3">$1 test pack for verifying the full Stripe → webhook → wallet → ledger pipeline. Hidden from customer pricing.</p>
              {pricing.packs.filter((p) => p.admin_only).map((p) => (
                <button
                  key={p.sku}
                  onClick={() => onBuyPack(p.sku)}
                  disabled={busyPack !== null}
                  data-testid={`pack-${p.sku}`}
                  className="inline-flex items-center gap-3 border border-purple-500/40 hover:border-purple-300 bg-[#121212] px-5 py-3 transition-colors disabled:opacity-50"
                >
                  <span className="font-display text-2xl font-bold text-[#F4F4F0]">{p.credits}</span>
                  <span className="text-xs text-[#A0A0A0]">credits</span>
                  <span className="text-[10px] uppercase tracking-wider text-purple-300 ml-2">
                    {busyPack === p.sku ? "Opening…" : `Buy $${p.price_usd} (test)`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <PricingMatrix matrix={pricing?.matrix} />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0]">Recent activity · last 30</p>
        <Link to="/templates" className="text-xs text-[#D4AF37] hover:underline uppercase tracking-wider inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Browse templates
        </Link>
      </div>
      <div className="border border-[#2A2A2A] bg-[#0F0F0F]" data-testid="credit-ledger">
        {ledger.length === 0 ? (
          <div className="px-5 py-10 text-center text-[#A0A0A0] text-sm">No activity yet. Run a generation to see it here.</div>
        ) : (
          <div className="divide-y divide-[#1A1A1A]">
            {ledger.map((row, i) => <LedgerRow key={i} row={row} />)}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#5A5A5A] mb-1">{label}</div>
      <div className={`font-display text-xl font-bold ${highlight ? "text-[#D4AF37]" : "text-[#F4F4F0]"}`}>{value}</div>
    </div>
  );
}

function RowStat({ label, value, icon: Icon, color }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs text-[#A0A0A0] flex items-center gap-2"><Icon className={`w-3.5 h-3.5 ${color}`} /> {label}</span>
      <span className="font-mono text-sm text-[#F4F4F0]">{value}</span>
    </div>
  );
}

function LedgerRow({ row }) {
  const sign = row.delta > 0 ? "+" : "";
  const color = row.kind === "grant" ? "text-emerald-300" : row.kind === "refund" ? "text-blue-300" : "text-[#F4F4F0]";
  const bonus = Number(row.bonus_amount || 0);
  const base  = Number(row.base_amount  || 0);
  return (
    <div className="px-5 py-3 flex items-center gap-3 text-xs">
      <span className="uppercase tracking-wider text-[10px] text-[#A0A0A0] w-16">{row.kind}</span>
      <span className="flex-1 text-[#A0A0A0]">
        {row.reason}
        {bonus > 0 && (
          <span className="ml-2 text-[10px] text-[#D4AF37]" data-testid="ledger-bonus-chip">
            · {base} base + {bonus} bonus
          </span>
        )}
      </span>
      <span className={`font-mono font-bold ${color}`}>{sign}{row.delta}</span>
      <span className="text-[10px] text-[#5A5A5A] font-mono w-32 text-right">{(row.created_at || "").slice(0, 16).replace("T", " ")}</span>
    </div>
  );
}

function PricingMatrix({ matrix }) {
  if (!matrix) return null;
  const tiers = ["draft", "pro", "cinema"];
  const items = [
    { key: "image",     label: "Image" },
    { key: "logo",      label: "Logo" },
    { key: "voice_30s", label: "Voice · 30s" },
    { key: "voice_60s", label: "Voice · 60s" },
    { key: "video_10s", label: "Video · 10s" },
    { key: "video_20s", label: "Video · 20s" },
    { key: "video_30s", label: "Video · 30s" },
    { key: "video_60s", label: "Video · 60s" },
  ];
  return (
    <div className="mb-10 border border-[#2A2A2A] bg-[#0F0F0F]" data-testid="pricing-matrix">
      <div className="px-5 py-3 border-b border-[#2A2A2A] flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0]">Credit pricing</p>
        <span className="text-[10px] text-[#5A5A5A] uppercase tracking-wider">3 quality tiers</span>
      </div>
      <div className="grid grid-cols-4 px-5 py-2 border-b border-[#1A1A1A] text-[10px] uppercase tracking-wider text-[#5A5A5A]">
        <span>Feature</span>
        <span className="text-center">Draft</span>
        <span className="text-center">Pro</span>
        <span className="text-center">Cinema</span>
      </div>
      {items.map((it) => {
        const row = matrix[it.key] || {};
        return (
          <div key={it.key} className="grid grid-cols-4 px-5 py-2.5 border-b border-[#1A1A1A] text-sm hover:bg-[#121212]">
            <span className="text-[#F4F4F0]">{it.label}</span>
            {tiers.map((q) => (
              <span key={q} className="text-center font-mono text-[#A0A0A0]">{row[q] ?? "—"} cr</span>
            ))}
          </div>
        );
      })}
    </div>
  );
}
